import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Warning,
  Plus,
  TrashSimple,
  Pencil,
  X,
  FileText,
  ImageSquare,
  Truck as TruckIcon,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { Check, CaretUpDown } from "@phosphor-icons/react";
import { PhotoLightbox } from "../components/PhotoLightbox";
import {
  fetchClaims,
  createClaim,
  updateClaim,
  deleteClaim,
  fetchTrucks,
  uploadPhoto,
  photoUrl,
} from "@/lib/api";
import { useAutoRefresh } from "@/lib/useAutoRefresh";

const STATUS_LABEL = {
  en_cours: "En cours",
  attente_expert: "Attente expert",
  clos: "Clos",
  sans_suite: "Sans suite",
};
const STATUS_COLOR = {
  en_cours: "#DC2626",
  attente_expert: "#F59E0B",
  clos: "#10B981",
  sans_suite: "#71717A",
};

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR");
}

export default function ClaimsPage() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [lightbox, setLightbox] = useState({ list: [], index: null });
  const [form, setForm] = useState({
    truck_id: "",
    date: new Date().toISOString().slice(0, 10),
    description: "",
    third_party_name: "",
    third_party_plate: "",
    third_party_insurance: "",
    location: "",
    estimated_cost: "",
    status: "en_cours",
    constat_doc: null,
    photos: [],
  });

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [c, t] = await Promise.all([fetchClaims(), fetchTrucks()]);
      setClaims(c);
      setTrucks(t);
    } catch {
      if (!silent) toast.error("Erreur de chargement");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const [searchParams, setSearchParams] = useSearchParams();
  // Auto-open the new-claim dialog when arriving with ?truck=<id> (shortcut from truck detail)
  useEffect(() => {
    const truckParam = searchParams.get("truck");
    if (truckParam && trucks.length > 0) {
      const found = trucks.find((t) => t.id === truckParam);
      if (found) {
        setEditing(null);
        setForm({
          truck_id: found.id,
          date: new Date().toISOString().slice(0, 10),
          description: "",
          third_party_name: "",
          third_party_plate: "",
          third_party_insurance: "",
          location: "",
          estimated_cost: "",
          status: "en_cours",
          constat_doc: null,
          photos: [],
        });
        setDialogOpen(true);
        // Remove the query param after consuming it
        searchParams.delete("truck");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [trucks]);

  const truckById = useMemo(() => {
    const m = {};
    for (const t of trucks) m[t.id] = t;
    return m;
  }, [trucks]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return claims.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (!q) return true;
      const truck = truckById[c.truck_id];
      const text = [
        truck?.license_plate, truck?.brand,
        c.description, c.third_party_name, c.third_party_plate, c.location,
      ].filter(Boolean).join(" ").toLowerCase();
      return text.includes(q);
    });
  }, [claims, statusFilter, query, truckById]);

  const counts = useMemo(() => {
    const c = { all: claims.length };
    for (const k of Object.keys(STATUS_LABEL)) c[k] = claims.filter((x) => x.status === k).length;
    return c;
  }, [claims]);

  const openNew = () => {
    setEditing(null);
    setForm({
      truck_id: trucks[0]?.id || "",
      date: new Date().toISOString().slice(0, 10),
      description: "",
      third_party_name: "",
      third_party_plate: "",
      third_party_insurance: "",
      location: "",
      estimated_cost: "",
      status: "en_cours",
      constat_doc: null,
      photos: [],
    });
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      truck_id: c.truck_id,
      date: (c.date || "").slice(0, 10),
      description: c.description || "",
      third_party_name: c.third_party_name || "",
      third_party_plate: c.third_party_plate || "",
      third_party_insurance: c.third_party_insurance || "",
      location: c.location || "",
      estimated_cost: c.estimated_cost ?? "",
      status: c.status,
      constat_doc: c.constat_doc || null,
      photos: Array.isArray(c.photos) ? c.photos : [],
    });
    setDialogOpen(true);
  };

  const onDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Fichier trop volumineux (max 8 MB)");
    setUploadingDoc(true);
    try {
      const { path } = await uploadPhoto(f);
      setForm((p) => ({ ...p, constat_doc: path }));
      toast.success("Constat enregistré");
    } catch { toast.error("Erreur d'upload"); }
    finally { setUploadingDoc(false); }
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const oversized = files.filter((f) => f.size > 8 * 1024 * 1024);
    if (oversized.length) return toast.error(`${oversized.length} fichier(s) trop volumineux (max 8 MB)`);
    setUploadingPhotos(true);
    try {
      const paths = await Promise.all(files.map((f) => uploadPhoto(f).then((r) => r.path)));
      setForm((p) => ({ ...p, photos: [...(p.photos || []), ...paths] }));
      toast.success(`${paths.length} photo(s) ajoutée(s)`);
    } catch {
      toast.error("Erreur d'upload photo");
    } finally {
      setUploadingPhotos(false);
      // Reset input so the same files can be re-selected later
      e.target.value = "";
    }
  };

  const removePhoto = (idx) => {
    setForm((p) => ({
      ...p,
      photos: (p.photos || []).filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    if (!form.truck_id) return toast.error("Sélectionner un camion");
    if (!form.date) return toast.error("Date requise");
    const body = {
      truck_id: form.truck_id,
      date: new Date(form.date + "T12:00:00").toISOString(),
      description: form.description,
      third_party_name: form.third_party_name,
      third_party_plate: form.third_party_plate,
      third_party_insurance: form.third_party_insurance,
      location: form.location,
      estimated_cost: parseFloat(form.estimated_cost || "0") || 0,
      status: form.status,
      constat_doc: form.constat_doc,
      photos: form.photos || [],
    };
    try {
      if (editing) await updateClaim(editing.id, body);
      else await createClaim(body);
      toast.success("Enregistré");
      setDialogOpen(false);
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (c) => {
    if (!window.confirm("Supprimer ce sinistre ?")) return;
    try {
      await deleteClaim(c.id);
      toast.success("Supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6" data-testid="claims-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="label-tag">NY Trans</div>
          <h1 className="serif-display text-4xl md:text-5xl mt-1">
            <Warning size={28} weight="bold" className="inline mr-2 -mt-1 text-red-600" />
            Sinistres
          </h1>
          <div className="ny-divider w-32 mt-2" />
        </div>
        <button onClick={openNew} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-2" data-testid="claim-new">
          <Plus size={14} weight="bold" /> Nouveau sinistre
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 p-3 mb-4 space-y-3" data-testid="claims-filters">
        <div className="relative">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Recherche immatriculation, tiers, lieu..."
            className="pl-9 rounded-none border-zinc-300"
            data-testid="claims-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["all", ...Object.keys(STATUS_LABEL)].map((k) => {
            const isActive = statusFilter === k;
            const label = k === "all" ? "Tous" : STATUS_LABEL[k];
            return (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-1.5 transition-none ${
                  isActive ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
                }`}
                data-testid={`claims-filter-${k}`}
              >
                {label}
                <span className={`px-1.5 text-[10px] mono ${isActive ? "bg-white text-zinc-900" : "bg-zinc-200 text-zinc-700"}`}>
                  {counts[k]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading && <div className="text-sm text-zinc-500 text-center py-10">Chargement…</div>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-dashed border-zinc-300 py-10 text-center">
            <Warning size={36} weight="duotone" className="mx-auto text-zinc-400 mb-2" />
            <div className="text-sm uppercase tracking-widest font-bold text-zinc-500">Aucun sinistre</div>
          </div>
        )}
        {!loading && filtered.map((c) => {
          const truck = truckById[c.truck_id];
          return (
            <div
              key={c.id}
              className="bg-white border border-zinc-300 hover:border-zinc-900 p-4 cursor-pointer"
              onClick={() => openEdit(c)}
              data-testid={`claim-row-${c.id}`}
            >
              <div className="flex flex-wrap items-start gap-3 justify-between">
                <div className="flex items-start gap-3">
                  <div className="bg-zinc-900 text-white p-2">
                    <TruckIcon size={18} weight="bold" />
                  </div>
                  <div>
                    <div className="font-bold text-base mono uppercase" data-testid={`claim-plate-${c.id}`}>
                      {truck?.license_plate || "—"}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {truck?.brand} · {fmt(c.date)} {c.location ? `· ${c.location}` : ""}
                    </div>
                    {c.description && (
                      <div className="text-sm mt-1 text-zinc-700 max-w-xl line-clamp-2">{c.description}</div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white" style={{ background: STATUS_COLOR[c.status] }}>
                    {STATUS_LABEL[c.status]}
                  </span>
                  {!!c.estimated_cost && (
                    <span className="text-xs font-mono">{c.estimated_cost.toFixed(2)} €</span>
                  )}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {c.third_party_name && (
                  <span className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-300">Tiers : <b>{c.third_party_name}</b></span>
                )}
                {c.third_party_plate && (
                  <span className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-300 mono">{c.third_party_plate}</span>
                )}
                {c.third_party_insurance && (
                  <span className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-300">Assu : {c.third_party_insurance}</span>
                )}
                {c.constat_doc && (
                  <span className="px-1.5 py-0.5 bg-zinc-900 text-white inline-flex items-center gap-1">
                    <FileText size={10} weight="bold" /> Constat
                  </span>
                )}
                {c.photos?.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightbox({ list: c.photos, index: 0 });
                    }}
                    className="px-1.5 py-0.5 bg-amber-600 text-white inline-flex items-center gap-1 hover:bg-amber-700"
                    data-testid={`claim-photos-badge-${c.id}`}
                    title={`${c.photos.length} photo(s) du sinistre — clic pour ouvrir`}
                  >
                    <ImageSquare size={10} weight="bold" /> {c.photos.length} photo{c.photos.length > 1 ? "s" : ""}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-lg w-[calc(100vw-1.5rem)] sm:w-auto max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
              <Warning size={22} weight="fill" className="text-red-600" />
              {editing ? "Modifier sinistre" : "Nouveau sinistre"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="label-tag">Camion *</Label>
                <TruckPicker
                  value={form.truck_id}
                  trucks={trucks}
                  onChange={(v) => setForm({ ...form, truck_id: v })}
                />
              </div>
              <div>
                <Label className="label-tag">Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none border-zinc-900" data-testid="claim-date" />
              </div>
              <div>
                <Label className="label-tag">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none border-zinc-900" data-testid="claim-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-tag">Description / circonstances</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-zinc-900" rows={3} placeholder="Décrire le sinistre" data-testid="claim-description" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-tag">Tiers — nom</Label>
                <Input value={form.third_party_name} onChange={(e) => setForm({ ...form, third_party_name: e.target.value })} className="rounded-none border-zinc-900" />
              </div>
              <div>
                <Label className="label-tag">Tiers — plaque</Label>
                <Input value={form.third_party_plate} onChange={(e) => setForm({ ...form, third_party_plate: e.target.value })} className="rounded-none border-zinc-900 mono" />
              </div>
              <div className="col-span-2">
                <Label className="label-tag">Tiers — assurance</Label>
                <Input value={form.third_party_insurance} onChange={(e) => setForm({ ...form, third_party_insurance: e.target.value })} className="rounded-none border-zinc-900" />
              </div>
              <div className="col-span-2">
                <Label className="label-tag">Lieu</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-none border-zinc-900" />
              </div>
              <div>
                <Label className="label-tag">Coût estimé (€)</Label>
                <Input type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} className="rounded-none border-zinc-900 mono" />
              </div>
            </div>

            <div>
              <Label className="label-tag">Constat (photo ou PDF)</Label>
              <label className="block bg-zinc-900 text-white text-center py-2 px-3 text-xs uppercase tracking-widest font-bold cursor-pointer hover:bg-zinc-700" data-testid="claim-constat-label">
                {uploadingDoc ? "Upload..." : (form.constat_doc ? "Remplacer le constat" : "Téléverser le constat")}
                <input type="file" accept="image/*,application/pdf" onChange={onDoc} className="hidden" data-testid="claim-constat-input" />
              </label>
              {form.constat_doc && (
                <div className="mt-2 border border-zinc-300 p-2 inline-flex items-center gap-2">
                  <FileText size={16} weight="bold" />
                  <a href={photoUrl(form.constat_doc)} target="_blank" rel="noreferrer" className="text-xs font-bold uppercase tracking-widest underline">Voir le constat</a>
                  <button onClick={() => setForm({ ...form, constat_doc: null })} className="text-xs font-bold uppercase text-red-600">Retirer</button>
                </div>
              )}
            </div>

            <div>
              <Label className="label-tag">
                Photos du sinistre {form.photos?.length > 0 && `(${form.photos.length})`}
              </Label>
              <label
                className="block bg-white border-2 border-dashed border-zinc-400 text-zinc-700 text-center py-3 px-3 text-xs uppercase tracking-widest font-bold cursor-pointer hover:border-zinc-900 hover:text-zinc-900"
                data-testid="claim-photos-label"
              >
                {uploadingPhotos ? "Upload..." : "+ Ajouter des photos (dégâts, tiers, contexte…)"}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPhotos}
                  className="hidden"
                  data-testid="claim-photos-input"
                />
              </label>
              {form.photos?.length > 0 && (
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {form.photos.map((p, idx) => (
                    <div
                      key={`${p}-${idx}`}
                      className="relative group border border-zinc-300 bg-zinc-50 aspect-square overflow-hidden"
                      data-testid={`claim-photo-${idx}`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setLightbox({ list: form.photos, index: idx })
                        }
                        className="w-full h-full block cursor-zoom-in"
                        aria-label={`Photo ${idx + 1}`}
                      >
                        <img
                          src={photoUrl(p)}
                          alt={`Sinistre ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-0.5 right-0.5 bg-red-600 text-white w-5 h-5 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Retirer cette photo"
                        data-testid={`claim-photo-remove-${idx}`}
                      >
                        <X size={11} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            {editing && (
              <button onClick={() => { remove(editing); setDialogOpen(false); }} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-2 mr-auto" data-testid="claim-delete">
                <TrashSimple size={14} weight="bold" /> Supprimer
              </button>
            )}
            {editing && (
              <button
                onClick={() => navigate(`/trucks/${editing.truck_id}`)}
                className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-2"
              >
                Voir camion →
              </button>
            )}
            <button onClick={() => setDialogOpen(false)} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-2">
              <X size={14} weight="bold" /> Annuler
            </button>
            <button onClick={save} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-2" data-testid="claim-save">
              <Pencil size={14} weight="bold" /> {editing ? "Mettre à jour" : "Créer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhotoLightbox
        photos={lightbox.list}
        index={lightbox.index}
        onClose={() => setLightbox({ list: [], index: null })}
        onPrev={() =>
          setLightbox((s) => ({
            ...s,
            index: (s.index - 1 + s.list.length) % s.list.length,
          }))
        }
        onNext={() =>
          setLightbox((s) => ({
            ...s,
            index: (s.index + 1) % s.list.length,
          }))
        }
      />
    </div>
  );
}


function TruckPicker({ value, trucks, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => trucks.find((t) => t.id === value), [trucks, value]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="w-full h-10 px-3 border border-zinc-900 bg-white text-sm flex items-center justify-between gap-1 hover:border-zinc-700 text-left"
          data-testid="claim-truck"
        >
          <span className={`truncate ${selected ? "" : "text-zinc-400"}`}>
            {selected
              ? `${selected.license_plate} · ${(selected.brand || "").toUpperCase()}`
              : "Sélectionner..."}
          </span>
          <CaretUpDown size={14} weight="bold" className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[360px]" align="start">
        <Command className="rounded-none">
          <CommandInput
            placeholder="Plaque, marque, modèle, site…"
            className="text-sm"
            data-testid="claim-truck-search"
          />
          <CommandList>
            <CommandEmpty>Aucun camion trouvé.</CommandEmpty>
            <CommandGroup>
              {trucks.map((t) => {
                const searchValue = `${t.license_plate} ${t.brand || ""} ${t.model || ""} ${t.site || ""}`.toLowerCase();
                const isSelected = t.id === value;
                return (
                  <CommandItem
                    key={t.id}
                    value={searchValue}
                    onSelect={() => {
                      onChange(t.id);
                      setOpen(false);
                    }}
                    className="rounded-none text-sm flex items-center gap-2"
                    data-testid={`claim-truck-option-${t.id}`}
                  >
                    <Check size={12} weight="bold" className={`shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`} />
                    <span className="font-bold mono uppercase">{t.license_plate}</span>
                    <span className="text-xs text-zinc-500 truncate">
                      {(t.brand || "").toUpperCase()} · {(t.site || "").toUpperCase()}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
